/* eslint new-cap:0 */
import Backbone from 'backbone';
import ko from 'knockout';
import _ from 'underscore';
import config from '@bingads-webui-component-legacy/config/index';
import {
  attachAIGenAssetContextualAttribute,
} from '@bingads-webui-campaign/responsive-search-ad-strength';
import ViewModel from '@bingads-webui-campaign-legacy/crud-ad/viewmodel/adsEditor/responsiveSearchAd';
import editorTemplate from '@bingads-webui-campaign-legacy/crud-ad/template/adsEditor/responsiveSearchAd.pug';
import editorHeadlineDescriptionOnlyTemplate from '@bingads-webui-campaign-legacy/crud-ad/template/adsEditor/responsiveSearchAdHeadlineDescriptionOnly.pug';
import { isCampaignEditorialError, isCampaignApiError } from '@bingads-webui-component-legacy/api-errors-campaign/index';
import buildPreview from '@bingads-webui-campaign-legacy/crud-ad/view/adsPreview/responsiveTextAd';
import buildAdStrength from '@bingads-webui-campaign-legacy/crud-ad/view/adsStrength/responsiveSearchAd';
import uuid from '@bingads-webui-component-legacy/uuid/index';
import UrlEditor from '@bingads-webui-component-legacy/url-input/view/url-input';
import UrlOptions from '@bingads-webui-component-legacy/upgraded-url/view/urlOptionsV2';
import { HeadlinesView, DescriptionsView } from '@bingads-webui-campaign-legacy/crud-ad/view/adsEditor/responsiveListField';
import { enableAdCustomizersHelpers } from './adCustomizersHelpers';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/campaign-creation-advanced');

const permissions = config.get('Permissions') || {};
const fieldsWithPlaceHolder = [
  'Path1',
  'Path2',
  'FinalUrls',
  'MobileFinalUrls',
  'Headline1',
  'Headline2',
  'Headline3',
  'Description1',
  'Description2',
].concat(permissions.IsVanityUrlEnabled ? ['DisplayUrl'] : []);
const placeholders = _.object(fieldsWithPlaceHolder, _.map(fieldsWithPlaceHolder, (field) => {
  return i18n.get(`ResponsiveSearchAd_Placeholder_${field}`);
}));
const fieldsWithLabel = ['Path1', 'Path2'];
const labels = _.object(fieldsWithLabel, _.map(fieldsWithLabel, (field) => {
  return i18n.get(`ResponsiveSearchAd_Label_${field}`);
}));
const {
  IsExpandedTextAdV2Enabled,
} = permissions;

/*For initial stage, MT converts the expanded text ad to standard text ad and passes it to editorial.
  So editorial will still return adtitle instead of titlepart1/titlepart2 as it doesn't know anything about EXTA.
  Once editorial is on boarded, will remove following mapping,
*/

// [TODO] need update
const fieldMapping = {
  'DisplayUrl': 'Path1',
  'DestinationUrl': 'FinalUrls',
};

const nameAttrs = {
  'Headline0': 'Headline0',
  'Headline1': 'Headline1',
  'Headline2': 'Headline2',
  'Headline3': 'Headline3',
  'Headline4': 'Headline4',
  'Headline5': 'Headline5',
  'Headline6': 'Headline6',
  'Headline7': 'Headline7',
  'Headline8': 'Headline8',
  'Headline9': 'Headline9',
  'Headline10': 'Headline10',
  'Headline11': 'Headline11',
  'Headline12': 'Headline12',
  'Headline13': 'Headline13',
  'Headline14': 'Headline14',
  'Description0': 'Description0',
  'Description1': 'Description1',
  'Description2': 'Description2',
  'Description3': 'Description3',
  'Path1': 'Ad.ExpandedTextAd.Path1',
  'Path2': 'Ad.ExpandedTextAd.Path2',
};

export default class View extends Backbone.View {
  initialize({
    showInline = false,
    editMode = false,
    model = null,
    // isNativeAdsEnabled = permissions.IsNativeAdsAllowed,
    isVanityUrlEnabled = permissions.IsVanityUrlEnabled,
    isTraditionalChinese = false,
    defaultLandingPageUrl = '',
    isForVNext = false,
    inlineSuggestionsEnabled = false,
    showLightbulb = false,
    scope = null,
    activityLogger,
    isImproveRSA = false,
    accountId = null,
  } = {}) {
    this.isForVNext = isForVNext;
    this.showInline = showInline;
    this.editMode = editMode;
    this.isNativeAdsEnabled = false; // TODO: enable when MT supports native ads for RSA
    this.isVanityUrlEnabled = isVanityUrlEnabled;
    this.isNativeAdsRebrandingEnabled = permissions.IsNativeAdsRebrandingEnabled;
    this.defaultLandingPageUrl = defaultLandingPageUrl;
    this.inlineSuggestionsEnabled = inlineSuggestionsEnabled;
    this.showLightbulb = showLightbulb;
    this.activityLogger = activityLogger;
    this.isImproveRSA = isImproveRSA;

    this.viewModel = new ViewModel({
      model,
      isNativeAdsEnabled: this.isNativeAdsEnabled,
      isVanityUrlEnabled,
      isTraditionalChinese,
      placeholders,
      defaultLandingPageUrl,
      IsExpandedTextAdV2Enabled,
      scope,
      activityLogger,
    });

    this.finalUrlEditor = new UrlEditor({
      url: _.first(_.result(this.model, 'FinalUrls')),
      ariaLabelledby: 'rsaFinalUrlLabel',
      ariaId: 'rsaFinalUrl',
      required: true,
    });
    this.mobileUrlEditor = new UrlEditor({
      url: _.first(_.result(this.model, 'MobileFinalUrls')),
      ariaLabelledby: 'rsaMobileUrlLabel',
      ariaId: 'rsaMobileUrl',
    });

    this.finalUrlEditor.on('urlChanged', (url) => {
      this.viewModel.FinalUrls(url ? [url] : []);
    });
    this.mobileUrlEditor.on('urlChanged', (url) => {
      this.viewModel.MobileFinalUrls(url ? [url] : []);
    });

    this.viewModel.FinalUrls.errors.subscribe((value) => {
      this.finalUrlEditor.hasErrors(!_.isEmpty(value));
    });

    this.viewModel.MobileFinalUrls.errors.subscribe((value) => {
      this.mobileUrlEditor.hasErrors(!_.isEmpty(value));
    });

    this.headlinesView = new HeadlinesView({ model: this.model ? this.model.Headlines : null, isForVNext, activityLogger, showLightbulb });
    this.descriptionsView = new DescriptionsView({ model: this.model ? this.model.Descriptions : null, isForVNext, activityLogger, showLightbulb });

    this.urlOptions = new UrlOptions({
      placeholderEnabled: true,
      showUurlTestTool: true,
      isForVNext,
      trackingTemplate: _.result(this.model, 'TrackingUrlTemplate'),
      finalUrlSuffix: _.result(this.model, 'FinalUrlSuffix'),
      customParameters: _.isEmpty(_.result(this.model, 'UrlCustomParameters')) ? undefined : _.clone(this.model.UrlCustomParameters),
      activityLogger,
    });

    this.urlOptions.on('testUurl', (fn, originalView) => {
      this.trigger('testUurl', fn, originalView);
    });

    this.previewContainer = buildPreview(_.extend({}, this.viewModel, this.headlinesView.viewModel, this.descriptionsView.viewModel));
    this.adStrengthContainer = buildAdStrength(this.editMode && this.viewModel.model ? this.viewModel.model.Id : undefined,
      _.extend({}, this.viewModel, this.headlinesView.viewModel, this.descriptionsView.viewModel, {accountId}),
      isForVNext, inlineSuggestionsEnabled);

    return this;
  }

  get events() {
    return {
      'click .prefer-native-format': 'preferNativeAdFormat',
    };
  }

  render() {
    if (this.isImproveRSA === false) {
      this.$el.html(editorTemplate({
        i18n,
        uuid,
        isNativeAdsEnabled: this.isNativeAdsEnabled,
        isVanityUrlEnabled: this.isVanityUrlEnabled,
        isNativeAdsRebrandingEnabled: this.isNativeAdsRebrandingEnabled,
        nameAttrs,
        labels,
        placeholders: {},
        domainWatermark: placeholders.FinalUrls,
      }));
    } else {
      this.$el.html(editorHeadlineDescriptionOnlyTemplate({
        i18n,
      }));
    }

    // [TODO] delete this debug line
    window.rv = this.viewModel;
    ko.applyBindings(this.viewModel, this.el);

    this.$('.final-url').empty().append(this.finalUrlEditor.render().el);
    this.$('.mobile-url').empty().append(this.mobileUrlEditor.render().el);
    this.$('.headlines-container').empty().append(this.headlinesView.render().el);
    this.$('.descriptions-container').empty().append(this.descriptionsView.render().el);
    this.$('.ext-ad-url-options').empty().append(this.urlOptions.render().el);

    this.$('.final-url input.url-field').attr('name', 'FinalUrls');
    this.$('.mobile-url input.url-field').attr('name', 'MobileFinalUrls');

    this.$('.preview-container').empty().append(this.previewContainer.render().el);
    this.previewContainer.selectedPanel(this.viewModel.initPreviewTab());

    this.$('.ad-strength-container').empty().append(this.adStrengthContainer.el);


    if (this.showLightbulb) {
      this.enableAIGenAssetContextualAttribute();
    }

    this.enableAdCustomizersHelpersForRSA();

    return this;
  }

  remove() {
    if (this.urlOptions) {
      this.urlOptions.remove();
    }
    this.finalUrlEditor.remove();
    this.mobileUrlEditor.remove();
    this.headlinesView.remove();
    this.descriptionsView.remove();
    this.previewContainer.remove();
    this.adStrengthContainer.remove();
    super.remove();
  }

  preferNativeAdFormat() {
    this.previewContainer.selectedPanel(this.viewModel.showNativeAdPreview() ? 'native' : 'top');
  }

  validateViewModel() {
    const isValid = this.viewModel.validate();

    if (!isValid) {
      _.each(this.viewModel.fields, (field) => {
        if (!this.viewModel[field].isValid()) {
          if (this.activityLogger) {
            this.activityLogger.signal(`ad field invalid: ${field}`, 'responsive search ad editor');
          }
        }
      });
    }

    return isValid;
  }

  validate() {
    return _.reduce([
      this.validateViewModel(),
      this.headlinesView.validate(),
      this.descriptionsView.validate(),
      _.isEmpty(this.viewModel.errorMessage()),
      this.urlOptions ? this.urlOptions.isValid() : true,
    ], (prev, next) => {
      return prev && next;
    }, true);
  }

  resetEditMode(mode) {
    this.editMode = mode;
  }

  toObject() {
    let params = _.omit(
      _.extend(this.viewModel.toObject(), this.urlOptions ? this.urlOptions.toObject() : {}),
      this.isVanityUrlEnabled ? [] : ['DisplayUrl'],
    );

    params.Headlines = this.headlinesView.toObject();
    params.Descriptions = this.descriptionsView.toObject();

    const api = this.showInline ? 'odata' : 'bulk';
    const arrayEmptyValue = api === 'odata' ? [] : null;
    const stringEmptyValue = api === 'odata' ? '' : null;

    //bulk API needs use 'delete_vlaue' to indicate deleting a value, in jsdata bulk adapter, we will replace null with 'delete_value' to remove value
    if (this.editMode) {
      const nullableArrayFields = [
        'FinalUrls',
        'MobileFinalUrls',
        'UrlCustomParameters',
      ];

      const nullableStringFields = _.compact([
        'TrackingUrlTemplate',
        'FinalUrlSuffix',
        'Path1',
        'Path2',
      ]);

      _.each(nullableArrayFields, (prop) => {
        if (_.isEmpty(params[prop])) {
          params[prop] = arrayEmptyValue;
        }
      });

      _.each(nullableStringFields, (prop) => {
        if (_.isEmpty(params[prop])) {
          params[prop] = stringEmptyValue;
        }
      });
    }

    return params;
  }

  // [TODO] need update
  handleApiErrors(errors) {
    const parsedErrors = _.reduce(errors, (memo, error) => {
      if (_.has(error, 'property') && isCampaignEditorialError(error)) {
        // This mapping is for editorial errors. Once editorial is on boarded, will remove following mapping. See bug 576573.
        error.property = fieldMapping[error.property] || error.property;
        return [...memo, error];
      }
      return [...memo, error];
    }, []);

    this.viewModel.errors.add(parsedErrors);
    this.headlinesView.viewModel.errors.add(parsedErrors);
    this.descriptionsView.viewModel.errors.add(parsedErrors);
    if (this.headlinesView.viewModel.errors().length === 0 || this.descriptionsView.viewModel.errors().length === 0) {
      this.viewModel.errors.clear(isCampaignApiError);
    }
    const errorFields = _.compact(['TrackingUrlTemplate', 'UrlCustomParameters', 'FinalUrlSuffix']);
    let errorObject = _.object(errorFields, _.map(errorFields, (key) => {
      return this.viewModel[key].errors();
    }));

    this.urlOptions.handleApiErrors(errorObject);
  }

  clearApiErrors() {
    this.viewModel.errors.clear(isCampaignApiError);
    this.urlOptions.clearApiErrors();
  }

  /**
   * Registers AI generated asset contextual attribute popup to listen for key/mouse events
   * @returns {undefined}
   */
  enableAIGenAssetContextualAttribute() {
    let self = this;

    _.each(_.range(15), (i) => {
      const element = self.$(`.iconba.iconba-Lightbulb.Headline${i}-AIGeneratedAsset`);

      attachAIGenAssetContextualAttribute(_.extend({
        element,
        i18n,
        isAIGeneratedAsset: !this.isImproveRSA,
      }));
    });

    _.each(_.range(4), (i) => {
      const element = self.$(`.iconba.iconba-Lightbulb.Description${i}-AIGeneratedAsset`);

      attachAIGenAssetContextualAttribute(_.extend({
        element,
        i18n,
        isAIGeneratedAsset: !this.isImproveRSA,
      }));
    });
  }

  /**
   * Registers ad customizers helpers to listen for key/mouse events on a number of form fields to pop themselves up when applicable
   * @returns {undefined}
   */
  enableAdCustomizersHelpersForRSA() {
    const customizableHeadlineFields = ['Headline0', 'Headline1', 'Headline2', 'Headline3', 'Headline4', 'Headline5', 'Headline6', 'Headline7', 'Headline8', 'Headline9', 'Headline10', 'Headline11', 'Headline12', 'Headline13', 'Headline14'];
    const customizableDescriptionFields = ['Description0', 'Description1', 'Description2', 'Description3'];
    const customizableFields = ['Path1', 'Path2'];

    enableAdCustomizersHelpers(this.headlinesView, customizableHeadlineFields, nameAttrs, 'ResponsiveSearchAd', this.$('> .ac-container'));
    enableAdCustomizersHelpers(this.descriptionsView, customizableDescriptionFields, nameAttrs, 'ResponsiveSearchAd', this.$('> .ac-container'));
    enableAdCustomizersHelpers(this, customizableFields, nameAttrs, 'ResponsiveSearchAd');
  }
}
